import { Either } from 'monet';
import {
  BonusCard,
  Bonuses,
  BonusSettings,
  CARD_TYPE_BONUS,
  CARD_TYPE_DISCOUNT,
  ClinicBonusSettings,
  DetailBonusCard,
  DetailDiscountCard,
  DiscountCard,
  DiscountLevel,
  DiscountSettings,
  Level, OrganizationLoyaltySystemSettings,
  OrganizationBonusSettings,
  Transaction,
} from '@/mixins/loyaltySystemMixin';
import LeftResponse from '@/storage/LeftResponse';

/**
 * @description Шлюз системы лояльности.
 * @param axios
 * @constructor
 */
export default function LoyaltySystemGateway(axios) {
  /**
   * @description Получить правила дискаунт карты
   * @returns {*}
   */
  this.getLevels = () => axios
    .get('/api/loyalty-system/discount-settings')
    .then(
      (response) => {
        const levels = response.data.levels.map((raw) => new Level(raw.id, '', raw.amount, raw.percentage));
        return Either.right(new DiscountSettings(levels, response.data.is_main_branch));
      },
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Получить настройки системы лояльности организации.
   * @returns {*}
   */
  this.viewOrganizationLoyaltySettings = () => axios
    .get('/api/loyalty-system/settings')
    .then(
      (response) => Either.right(
        new OrganizationLoyaltySystemSettings(response.data.loyalty_system_mode),
      ),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );
  /**
   * @description Сохранить настройки системы лояльности организации.
   * @param {Object} request
   * @returns {*}
   */
  this.storeOrganizationLoyaltySettings = (request) => axios
    .post(
      '/api/loyalty-system/settings',
      {
        loyalty_system_mode: request.loyaltySystemMode,
      },
    )
    .then(
      (response) => Either.right(response.data),
      (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          return Either.Left(error.response.data);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить дискаунт карту.
   * @param {DiscountSettings} discountCard
   * @returns {*}
   */
  this.storeLevels = (discountCard) => axios
    .post(
      '/api/loyalty-system/discount-settings',
      {
        levels: discountCard.level.map((el) => ({
          id: el.id,
          amount: el.amount,
          percentage: el.percentage,
          tmp_id: el.tmpId,
        })),
      },
    )
    .then(
      (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const {
            causes,
            title,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Удалить правило
   * @param {string} id
   * @returns
   */
  this.deleteLevel = (id) => axios
    .delete(`/api/loyalty-system/discount-settings/level/${id}`)
    .then(
      (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const {
            causes,
            title,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Получить настройки бонусной системы клиники.
   * @returns {*}
   */
  this.getClinicBonusSettings = () => axios
    .get('/api/loyalty-system/bonus-settings')
    .then(
      (response) => {
        const settings = new BonusSettings(
          response.data.is_main_branch,
          new OrganizationBonusSettings(
            response.data.organization_bonus_settings.lifetime_bonus_days > 0,
            response.data.organization_bonus_settings.lifetime_bonus_days,
            response.data.organization_bonus_settings.welcome_bonus_amount > 0,
            new Bonuses(response.data.organization_bonus_settings.welcome_bonus_amount),
            response.data.organization_bonus_settings.birthday_bonus_amount > 0,
            new Bonuses(response.data.organization_bonus_settings.birthday_bonus_amount),
            response.data.organization_bonus_settings.payment_limit_percent !== 100,
            response.data.organization_bonus_settings.payment_limit_percent,
            response.data.organization_bonus_settings.is_accrue_on_bonus_payment,
            response.data.organization_bonus_settings.accrual_bonus_percent,
          ),
          new ClinicBonusSettings(
            response.data.clinic_bonus_settings.excluded_redemption_service_ids,
            response.data.clinic_bonus_settings.excluded_accrual_service_ids,
          ),
        );
        return Either.right(settings);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const {
            causes,
            title,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );

  /**
   * @description Сохранить настройки бонусный системы
   * @param {BonusSettings} request
   * @returns {Either}
   */
  this.storeBonusSettings = (request) => axios
    .post(
      '/api/loyalty-system/bonus-settings',
      {
        lifetime_bonus_days: request.organizationBonusSettings.lifetimeBonusDays !== ''
          ? request.organizationBonusSettings.lifetimeBonusDays
          : 0,
        welcome_bonus_amount: request.organizationBonusSettings.welcomeBonusAmount.amount,
        birthday_bonus_amount: request.organizationBonusSettings.birthdayBonusAmount.amount,
        payment_limit_percent: request.organizationBonusSettings.paymentLimitPercent,
        is_accrue_on_bonus_payment: request.organizationBonusSettings.accrueOnBonusPayment,
        accrual_bonus_percent: request.organizationBonusSettings.accrualBonusPercent,
        excluded_accrual_service_ids: request.clinicBonusSettings.excludedAccrualServiceIds,
        excluded_redemption_service_ids: request.clinicBonusSettings.excludedRedemptionServiceIds,
      },
    )
    .then(
      (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const {
            causes,
            title,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Создать бонусную карту
   * @param bonusCard
   * @returns {Either}
   */
  this.createPatientBonusCard = (bonusCard) => axios
    .post('/api/loyalty-system/patient/bonus-cards', {
      number: bonusCard.number,
      amount: bonusCard.amount,
      patient_id: bonusCard.patientId,
    })
    .then(
      (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const {
            causes,
            title,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Создать бонусную карту
   * @param discountCard
   * @returns {Either}
   */
  this.createPatientDiscountCard = (discountCard) => axios
    .post('/api/loyalty-system/patient/discount-cards', {
      number: discountCard.number,
      patient_id: discountCard.patientId,
    })
    .then(
      (response) => Either.right(response.data),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const {
            causes,
            title,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Просмотреть бонусную карту пациента
   * @param patientId
   * @returns {*}
   */
  this.viewPatientLoyaltyCard = (patientId) => axios
    .get(`/api/loyalty-system/patient/${patientId}/card`)
    .then(
      (response) => {
        if (!response.data.card) {
          return Either.right({ card: new BonusCard() });
        }
        if (response.data.card.type === CARD_TYPE_BONUS) {
          const card = new BonusCard(
            response.data.card.number,
            response.data.card.patient_id,
            response.data.card.amount,
            response.data.card.type,
            response.data.card.type_name,
            response.data.is_card_created,
          );
          return Either.right({ card });
        }
        if (response.data.card.type === CARD_TYPE_DISCOUNT) {
          const card = new DiscountCard(
            response.data.card.number,
            response.data.card.patient_id,
            new DiscountLevel(
              response.data.card.level.threshold_amount,
              response.data.card.level.discount_percent,
            ),
            response.data.card.type,
            response.data.card.type_name,
            response.data.is_card_created,
          );
          return Either.right({ card });
        }
        return Either.right();
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const {
            causes,
            title,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Просмотреть детализацию по бонусной карте пациента
   * @param patientId
   * @returns {*}
   */
  this.viewDetailBonusCard = (patientId) => axios
    .get(`/api/loyalty-system/patient/${patientId}/bonus-card-detail`)
    .then(
      (response) => {
        const transaction = response
          .data
          .transactions
          .map((transactionRaw) => new Transaction(
            transactionRaw.id,
            transactionRaw.created_at,
            transactionRaw.amount,
            transactionRaw.source_type,
            transactionRaw.type,
          ));
        const detailBonusCard = new DetailBonusCard(
          response.data.number,
          transaction,
        );
        return Either.right(detailBonusCard);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const {
            causes,
            title,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Просмотреть детализацию по скидочной карте пациента
   * @param patientId
   * @returns {*}
   */
  this.viewDetailDiscountCard = (patientId) => axios
    .get(`/api/loyalty-system/patient/${patientId}/discount-card-detail`)
    .then(
      (response) => {
        const detailDiscountCard = new DetailDiscountCard(
          response.data.number,
          response.data.current_discount_percent,
          response.data.amount_to_next_level,
        );
        return Either.right(detailDiscountCard);
      },
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const {
            causes,
            title,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
  /**
   * @description Просмотреть детализацию по бонусной карте пациента
   * @param {Object} request
   * @param {number} invoiceId
   * @returns {*}
   */
  this.fetchLimitBonuses = (request, invoiceId) => axios
    .post(
      `/api/loyalty-system/invoice/${invoiceId}/bonus-limit`,
      { positions: request.positions },
    )
    .then(
      (response) => Either.right({
        isCanFullyPayByBonuses: response.data.is_can_fully_pay,
        limitBonuses: response.data.limit_bonuses,
      }),
      async (error) => {
        if (error.isAxiosError && [400, 403].includes(error.response.status)) {
          const {
            causes,
            title,
          } = error.response.data;
          const leftResponse = new LeftResponse(causes, title);
          return Either.left(leftResponse);
        }
        throw error;
      },
    );
}
